import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Bookmark,
  BookmarkPlus,
  Check,
  Link2,
  Pencil,
  Star,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import Modal from './Modal';
import { apiSavedViews } from '../api/client';
import { toFilterQuery, useFilterStore } from '../hooks/useFilterStore';
import { readSort, sortDescriptorForScope, writeSort } from '../hooks/useSortStore';

/**
 * Ticket #37 — Vues de filtre sauvegardées.
 *
 * Complète la `FilterBar` : le filtre courant reste dans `useFilterStore`
 * (localStorage, un seul jeu de conditions actif par scope) ; ce contrôle
 * ajoute une **collection de vues nommées** persistées côté serveur, donc
 * réutilisables d'une session — et d'un poste — à l'autre.
 *
 *  - « Vues » ouvre un popover : appliquer / définir par défaut / partager /
 *    copier le lien / renommer / supprimer.
 *  - « Enregistrer » n'apparaît que si au moins une condition est active.
 *  - Une vue mémorise **les filtres et le tri** de l'écran (le tri vit dans
 *    `useSortStore` ; les scopes sans colonne triable n'en mémorisent aucun).
 *  - Au premier montage d'un scope : un lien `?view=<id>` prime, sinon la vue
 *    marquée par défaut est appliquée si aucune condition n'est posée.
 *
 * Props :
 *  - scope : même valeur que celle passée à `FilterBar`.
 */
export default function SavedViewsControl({ scope }) {
  const { conditions, replaceConditions } = useFilterStore(scope);
  // Descripteur du tri de l'écran porteur (null si l'écran n'a pas de tri).
  const sortDescriptor = sortDescriptorForScope(scope);

  const [views, setViews] = useState([]);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [error, setError] = useState(null);
  // Modale de création / renommage : { mode: 'create'|'rename', view? }
  const [dialog, setDialog] = useState(null);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const triggerRef = useRef(null);
  // Une seule application automatique de la vue par défaut par scope monté.
  const autoAppliedRef = useRef(false);
  // `conditions` dans une ref : l'auto-application ne doit pas se rejouer à
  // chaque édition du filtre (sinon on écraserait la saisie de l'utilisateur).
  const conditionsRef = useRef(conditions);
  conditionsRef.current = conditions;

  const reload = useCallback(async () => {
    try {
      const res = await apiSavedViews.list(scope);
      const items = res.items ?? [];
      setViews(items);
      return items;
    } catch {
      setViews([]);
      return [];
    }
  }, [scope]);

  useEffect(() => {
    let cancelled = false;
    autoAppliedRef.current = false;

    (async () => {
      const items = await reload();
      if (cancelled || autoAppliedRef.current) return;
      autoAppliedRef.current = true;

      // 1) Lien partagé `?view=<id>` : prime sur tout, et prime sur le filtre
      //    courant (l'utilisateur a cliqué un lien, il attend CETTE vue).
      const linkedId = readViewIdFromUrl();
      if (linkedId) {
        const linked = items.find((v) => v.id === linkedId);
        if (linked) {
          applyView(linked, { closePopover: false });
          return;
        }
        // La vue n'est pas dans la liste du scope : soit elle appartient à un
        // autre écran, soit elle n'est pas partagée — on tente une lecture
        // directe (404 si elle n'est pas accessible) et on n'applique que si
        // elle concerne bien ce scope.
        try {
          const view = await apiSavedViews.get(linkedId);
          if (!cancelled && view?.scope === scope) {
            applyView(view, { closePopover: false });
            return;
          }
        } catch {
          // Vue inaccessible / supprimée — on retombe sur la vue par défaut.
        }
        if (cancelled) return;
      }

      // 2) Vue par défaut, seulement si l'écran n'a pas déjà un filtre actif.
      const def = items.find((v) => v.isDefault);
      if (def && (conditionsRef.current ?? []).length === 0) {
        applyView(def, { closePopover: false });
      }
    })();

    return () => {
      cancelled = true;
    };
    // `applyView` est stable (useCallback sur `replaceConditions`/`scope`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, reload]);

  // Ré-hydrate les conditions avec un `id` local (le backend ne le stocke pas)
  // et réapplique le tri mémorisé s'il y en a un.
  const applyView = useCallback(
    (view, { closePopover = true } = {}) => {
      const next = (view.conditions ?? []).map((c, i) => ({
        ...c,
        id: `${view.id}:${i}`,
      }));
      replaceConditions(next);
      if (sortDescriptor && view.sort) writeSort(sortDescriptor, view.sort);
      if (closePopover) setOpen(false);
    },
    [replaceConditions, sortDescriptor],
  );

  function openPopover() {
    const rect = triggerRef.current?.getBoundingClientRect();
    setAnchor(rect ? { bottom: rect.bottom, left: rect.left } : null);
    setError(null);
    setOpen((v) => !v);
  }

  function openCreate() {
    setName('');
    setShared(false);
    setError(null);
    setDialog({ mode: 'create' });
    setOpen(false);
  }

  function openRename(view) {
    setName(view.name);
    setError(null);
    setDialog({ mode: 'rename', view });
    setOpen(false);
  }

  async function submitDialog(e) {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Le nom est requis.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (dialog.mode === 'create') {
        const payload = toFilterQuery(conditions);
        if (!payload) {
          setError('Aucun filtre actif à enregistrer.');
          return;
        }
        await apiSavedViews.create({
          scope,
          name: trimmed,
          conditions: payload.conditions,
          sort: sortDescriptor ? readSort(sortDescriptor) : null,
          isShared: shared,
        });
      } else {
        await apiSavedViews.update(dialog.view.id, { name: trimmed });
      }
      await reload();
      setDialog(null);
    } catch (err) {
      setError(err?.message ?? 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }

  async function updateCurrent(view) {
    const payload = toFilterQuery(conditions);
    if (!payload) return;
    try {
      await apiSavedViews.update(view.id, {
        conditions: payload.conditions,
        sort: sortDescriptor ? readSort(sortDescriptor) : null,
      });
      await reload();
      setOpen(false);
    } catch (err) {
      setError(err?.message ?? 'Mise à jour impossible.');
    }
  }

  async function toggleDefault(view) {
    try {
      await apiSavedViews.update(view.id, { isDefault: !view.isDefault });
      await reload();
    } catch (err) {
      setError(err?.message ?? 'Mise à jour impossible.');
    }
  }

  async function toggleShared(view) {
    try {
      await apiSavedViews.update(view.id, { isShared: !view.isShared });
      await reload();
    } catch (err) {
      setError(err?.message ?? 'Mise à jour impossible.');
    }
  }

  async function copyLink(view) {
    const url = buildViewUrl(view.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(view.id);
      window.setTimeout(() => setCopiedId((v) => (v === view.id ? null : v)), 2000);
    } catch {
      // Presse-papier indisponible (contexte non sécurisé, permission refusée) :
      // on affiche l'URL pour que l'utilisateur puisse la copier à la main.
      setError(url);
    }
  }

  async function remove(view) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Supprimer la vue « ${view.name} » ?`)) return;
    try {
      await apiSavedViews.remove(view.id);
      await reload();
    } catch (err) {
      setError(err?.message ?? 'Suppression impossible.');
    }
  }

  const hasConditions = (conditions ?? []).length > 0;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPopover}
        title="Vues enregistrées"
        style={btnStyle}
      >
        <Bookmark size={14} />
        Vues
        {views.length > 0 && (
          <span style={countStyle}>{views.length}</span>
        )}
      </button>

      {hasConditions && (
        <button
          type="button"
          onClick={openCreate}
          title="Enregistrer le filtre courant comme vue"
          style={btnStyle}
        >
          <BookmarkPlus size={14} />
          Enregistrer
        </button>
      )}

      {open &&
        ReactDOM.createPortal(
          <ViewsPopover
            anchor={anchor}
            views={views}
            error={error}
            hasConditions={hasConditions}
            onClose={() => setOpen(false)}
            onApply={applyView}
            onRename={openRename}
            onToggleDefault={toggleDefault}
            onToggleShared={toggleShared}
            onCopyLink={copyLink}
            copiedId={copiedId}
            onUpdateCurrent={updateCurrent}
            onRemove={remove}
            onCreate={openCreate}
          />,
          document.body,
        )}

      <Modal
        open={dialog !== null}
        onClose={() => setDialog(null)}
        title={dialog?.mode === 'rename' ? 'Renommer la vue' : 'Enregistrer la vue'}
        subtitle={
          dialog?.mode === 'rename'
            ? null
            : `${conditions.length} condition(s)${
                sortDescriptor ? ' et le tri courant' : ''
              } seront enregistrées.`
        }
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setDialog(null)} style={btnStyle}>
              Annuler
            </button>
            <button
              type="button"
              onClick={submitDialog}
              disabled={saving}
              style={{ ...btnStyle, background: 'var(--primary)', color: '#fff' }}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        }
      >
        <form onSubmit={submitDialog}>
          <label
            htmlFor="saved-view-name"
            style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}
          >
            Nom de la vue
          </label>
          <input
            id="saved-view-name"
            type="text"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex. Mon équipe"
            style={{
              width: '100%',
              marginTop: 6,
              padding: '8px 10px',
              background: 'var(--glass)',
              color: 'var(--text)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 13,
            }}
          />
          {dialog?.mode === 'create' && (
            <button
              type="button"
              onClick={() => setShared((v) => !v)}
              aria-pressed={shared}
              title="Une vue partagée est visible par tous les utilisateurs ; vous seul pouvez la modifier."
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 12,
                padding: '6px 12px',
                background: shared ? 'var(--accent)' : 'var(--glass)',
                color: shared ? '#fff' : 'var(--text)',
                border: `1px solid ${shared ? 'var(--accent)' : 'var(--glass-border)'}`,
                borderRadius: 'var(--radius-pill)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Users size={14} />
              Partager avec l’équipe
            </button>
          )}

          {error && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)', wordBreak: 'break-all' }}>
              {error}
            </div>
          )}
        </form>
      </Modal>
    </>
  );
}

// ---------- Popover ----------

function ViewsPopover({
  anchor,
  views,
  error,
  hasConditions,
  copiedId,
  onClose,
  onApply,
  onRename,
  onToggleDefault,
  onToggleShared,
  onCopyLink,
  onUpdateCurrent,
  onRemove,
  onCreate,
}) {
  const shellRef = useRef(null);

  useEffect(() => {
    function onDocPointer(e) {
      if (!shellRef.current) return;
      if (!shellRef.current.contains(e.target)) onClose();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const top = anchor ? anchor.bottom + 6 : 60;
  const left = anchor ? Math.min(anchor.left, window.innerWidth - 380) : 16;

  return (
    <div
      ref={shellRef}
      role="dialog"
      style={{
        position: 'fixed',
        top,
        left,
        width: 360,
        maxHeight: 'min(420px, calc(100vh - 80px))',
        background: 'var(--card-bg)',
        color: 'var(--text)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '8px 10px',
          borderBottom: '1px solid var(--glass-border)',
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        Vues enregistrées
      </div>

      <div style={{ overflowY: 'auto', padding: 6, flex: 1 }}>
        {views.length === 0 && (
          <div style={{ padding: '12px 10px', fontSize: 13, color: 'var(--text-muted)' }}>
            Aucune vue enregistrée. Posez un filtre puis cliquez sur
            «&nbsp;Enregistrer&nbsp;».
          </div>
        )}

        {views.map((view) => (
          <div
            key={view.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 6px',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <button
              type="button"
              onClick={() => onApply(view)}
              title="Appliquer cette vue"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minWidth: 0,
                padding: '6px 6px',
                background: 'transparent',
                color: 'var(--text)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontSize: 13,
                textAlign: 'left',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--glass)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {view.name}
              </span>
              {view.isShared && (
                <Users
                  size={12}
                  style={{ flexShrink: 0, color: 'var(--accent)' }}
                  aria-label="Vue partagée"
                />
              )}
              {!view.isOwner && view.ownerName && (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  · {view.ownerName}
                </span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
                {view.conditions?.length ?? 0}
              </span>
            </button>

            <IconButton
              title={copiedId === view.id ? 'Lien copié' : 'Copier le lien de la vue'}
              onClick={() => onCopyLink(view)}
              color={copiedId === view.id ? 'var(--success)' : undefined}
            >
              {copiedId === view.id ? <Check size={14} /> : <Link2 size={14} />}
            </IconButton>

            {/* Les actions ci-dessous n'ont de sens que pour le propriétaire :
                une vue partagée d'autrui est en lecture seule (le backend
                répond 404 sur toute écriture). */}
            {view.isOwner && (
              <>
                <IconButton
                  title={view.isShared ? 'Ne plus partager' : 'Partager la vue'}
                  onClick={() => onToggleShared(view)}
                  color={view.isShared ? 'var(--accent)' : undefined}
                >
                  <Users size={14} />
                </IconButton>

                <IconButton
                  title={
                    view.isDefault ? 'Retirer des vues par défaut' : 'Définir par défaut'
                  }
                  onClick={() => onToggleDefault(view)}
                  color={view.isDefault ? 'var(--warning)' : undefined}
                >
                  <Star size={14} fill={view.isDefault ? 'var(--warning)' : 'none'} />
                </IconButton>

                {hasConditions && (
                  <IconButton
                    title="Remplacer par le filtre et le tri courants"
                    onClick={() => onUpdateCurrent(view)}
                  >
                    <Check size={14} />
                  </IconButton>
                )}

                <IconButton title="Renommer" onClick={() => onRename(view)}>
                  <Pencil size={14} />
                </IconButton>

                <IconButton
                  title="Supprimer"
                  onClick={() => onRemove(view)}
                  color="var(--danger)"
                >
                  <Trash2 size={14} />
                </IconButton>
              </>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div
          style={{
            padding: '6px 10px',
            fontSize: 12,
            color: 'var(--danger)',
            borderTop: '1px solid var(--glass-border)',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px',
          borderTop: '1px solid var(--glass-border)',
        }}
      >
        <button
          type="button"
          onClick={onCreate}
          disabled={!hasConditions}
          title={
            hasConditions
              ? 'Enregistrer le filtre courant'
              : 'Posez d’abord au moins un filtre'
          }
          style={{
            ...btnStyle,
            opacity: hasConditions ? 1 : 0.5,
            cursor: hasConditions ? 'pointer' : 'not-allowed',
          }}
        >
          <BookmarkPlus size={14} />
          Enregistrer le filtre courant
        </button>
        <IconButton title="Fermer" onClick={onClose}>
          <X size={14} />
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({ title, onClick, color, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 4,
        background: 'transparent',
        color: color ?? 'var(--text-muted)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Lien partageable : on ajoute `?view=<id>` à l'URL courante. L'app est une SPA
 * sans router — c'est `App.jsx` qui lit ce paramètre au démarrage pour ouvrir
 * le bon écran, puis ce composant qui applique la vue.
 */
function buildViewUrl(id) {
  const url = new URL(window.location.href);
  url.searchParams.set('view', id);
  return url.toString();
}

export function readViewIdFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get('view');
  } catch {
    return null;
  }
}

const btnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  cursor: 'pointer',
};

const countStyle = {
  padding: '0 6px',
  background: 'var(--glass)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-pill)',
  fontSize: 11,
  color: 'var(--text-muted)',
};
